import { useState } from 'react';
import type { Lead, NoteEntry } from '@shared/types';
import { formatDateTime } from '@shared/dates';
import { useAddNote, useEditNote, useStages } from '../store';
import { Icons, Popover } from './ui';

/**
 * The הערות cell: a bubble with a count; hover shows the last note; click opens a composer.
 * The count includes the conversation's opening message, so a WhatsApp lead with nothing else does not look empty.
 */
export function NotesBubble({ lead }: { lead: Lead }) {
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  // lastNote falls back to the opening message when there is nothing newer; don't show that text twice.
  const lastIsFirst = !!lead.firstMessage && lead.lastNote === lead.firstMessage;
  return (
    <>
      <button
        ref={setAnchor}
        type="button"
        className={`relative inline-flex h-11 min-w-11 items-center desk:h-8 desk:min-w-8 justify-center gap-1 rounded-full px-2 ${lead.noteCount ? 'bg-accent-soft text-accent' : 'text-ink-3 hover:bg-page'}`}
        title={lead.lastNote ? `${lastIsFirst ? 'הודעה ראשונה' : 'הערה אחרונה'}: ${lead.lastNote}` : 'הוסף הערה'}
        aria-label={`הערות (${lead.noteCount})`}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
      >
        <Icons.Note size={16} />
        {lead.noteCount > 0 && <span className="text-xs font-semibold">{lead.noteCount}</span>}
      </button>
      <Popover open={open} anchor={anchor} onClose={() => setOpen(false)} title="הוסף הערה">
        <div className="w-72 max-w-full" onClick={(e) => e.stopPropagation()}>
          {lead.firstMessage && (
            <div className="mb-2 rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-900">
              <div className="mb-0.5 text-[11px] text-emerald-700">הודעה ראשונה מהלקוח</div>
              <div className="line-clamp-4 whitespace-pre-wrap">{lead.firstMessage}</div>
            </div>
          )}
          {lead.lastNote && !lastIsFirst && (
            <div className="mb-2 rounded-lg bg-page p-2 text-xs text-ink-2">
              <div className="mb-0.5 text-[11px] text-ink-3">הערה אחרונה</div>
              <div className="line-clamp-3 whitespace-pre-wrap">{lead.lastNote}</div>
            </div>
          )}
          <NoteComposer leadId={lead.id} onDone={() => setOpen(false)} autoFocus />
        </div>
      </Popover>
    </>
  );
}

export function NoteComposer({ leadId, onDone, autoFocus }: { leadId: string; onDone?: () => void; autoFocus?: boolean }) {
  const [text, setText] = useState('');
  const add = useAddNote();
  const submit = () => {
    const t = text.trim();
    if (!t) return;
    // optimistic: the note shows at once and the composer is free; if the CRM refuses it, the text comes back here
    add.mutate({ id: leadId, text: t }, { onError: () => setText((cur) => (cur.trim() ? cur : t)) });
    setText('');
    onDone?.();
  };
  return (
    <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="grid gap-2">
      <textarea className="input" rows={3} placeholder="כתוב הערה…" value={text} onChange={(e) => setText(e.target.value)} autoFocus={autoFocus}
        onKeyDown={(e) => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') submit(); }} />
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-ink-3">Ctrl+Enter לשליחה</span>
        <button type="submit" className="btn btn-primary btn-sm" disabled={!text.trim()}>הוסף</button>
      </div>
    </form>
  );
}

const NOTE_TONE: Record<NoteEntry['kind'], string> = {
  blob: 'border-amber-200 bg-amber-50',
  comment: 'border-line bg-panel',
  'first-inbound': 'border-emerald-200 bg-emerald-50',
  'first-outbound': 'border-sky-200 bg-sky-50',
};
const NOTE_LABEL: Partial<Record<NoteEntry['kind'], string>> = {
  blob: 'הערות מהייבוא',
  'first-inbound': 'הודעה ראשונה מהלקוח',
  'first-outbound': 'הודעה ראשונה שנשלחה',
};

export function NotesTimeline({ notes, leadId }: { notes: NoteEntry[]; leadId: string }) {
  const { userName } = useStages();
  const [editing, setEditing] = useState<string | null>(null);
  if (notes.length === 0) return <p className="text-sm text-ink-3">אין הערות עדיין</p>;
  return (
    <ol className="grid gap-2">
      {[...notes].reverse().map((n) => (
        <li key={n.id} className={`rounded-lg border p-2 text-sm ${NOTE_TONE[n.kind]} ${n.id.startsWith('pending_') ? 'opacity-60' : ''}`}>
          <div className="mb-1 flex items-center justify-between gap-2 text-[11px] text-ink-3">
            <span>{NOTE_LABEL[n.kind] ?? (n.authorUserId ? userName(n.authorUserId) : 'מערכת')}</span>
            <span className="flex items-center gap-1.5">
              {n.edited && (
                <span title={`נערך ${formatDateTime(n.edited.at)}${n.edited.by && n.edited.by !== n.authorUserId ? ` ע״י ${userName(n.edited.by)}` : ''}`}>(נערך)</span>
              )}
              <span className="ltr">{formatDateTime(n.createdAt)}</span>
              {n.canEdit && editing !== n.id && !n.id.startsWith('pending_') && (
                <button type="button" className="rounded p-0.5 text-ink-3 hover:bg-page hover:text-accent" title="ערוך הערה" aria-label="ערוך הערה" onClick={() => setEditing(n.id)}>
                  <Icons.EditDoc size={13} />
                </button>
              )}
            </span>
          </div>
          {editing === n.id
            ? <NoteEditor leadId={leadId} note={n} onDone={() => setEditing(null)} />
            : <div className="whitespace-pre-wrap break-words">{n.body}</div>}
        </li>
      ))}
    </ol>
  );
}

function NoteEditor({ leadId, note, onDone }: { leadId: string; note: NoteEntry; onDone: () => void }) {
  const [text, setText] = useState(note.body);
  const edit = useEditNote();
  const changed = text.trim() && text.trim() !== note.body.trim();
  const submit = () => {
    if (changed) edit.mutate({ id: leadId, noteId: note.id, text: text.trim() }); // optimistic: an error reverts it
    onDone();
  };
  return (
    <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="grid gap-2">
      <textarea className="input" rows={3} value={text} onChange={(e) => setText(e.target.value)} autoFocus
        onKeyDown={(e) => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') submit(); if (e.key === 'Escape') onDone(); }} />
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] text-ink-3">העריכה נשמרת ב־CRM כהערת תיקון</span>
        <span className="flex gap-1.5">
          <button type="button" className="btn btn-sm" onClick={onDone}>ביטול</button>
          <button type="submit" className="btn btn-primary btn-sm" disabled={!changed}>שמור</button>
        </span>
      </div>
    </form>
  );
}
